import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { catchError, forkJoin, map, of, Subject, Subscription } from 'rxjs';
import { ActivatedRoute, Params } from '@angular/router';
import { environment } from '../../../environments/environment';
import { PanelModule } from 'primeng/panel';
import { DashboardHeaderComponent } from './components/dashboard-header/dashboard-header.component';
import {
  AvgClaimDRG,
  ChartDataService,
  ClaimRateByProvider,
  ClaimsByClinic,
  HospDurationDRG,
  MedicalExpensesProviderPercentage,
  PaginatedTableComponent,
  PanelComponent,
  PanelConfigI,
  ProviderClaim,
  ReportDataService,
  SplineChartComponent,
  DashboardFiltersComponent,
  PaginatedProvidersResponse
} from 'shared-library';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { TranslationService } from 'shared-library';
import { TableLazyLoadEvent } from 'primeng/table';


const pct = (n: number | null | undefined) => n ?? 0;
const norm = (s: string) => s?.trim().toUpperCase();

const CATEGORY_TO_FIELD: Record<string, keyof MedicalExpensesProviderPercentage> = {
  [norm('ΥΓΕΙΟΝΟΜΙΚΑ ΥΛΙΚΑ')]: 'medicalSuppliesPercentage',
  [norm('ΕΙΔΙΚΑ ΥΓΕΙΟΝΟΜΙΚΑ ΥΛΙΚΑ')]: 'specialMedicalSuppliesPercentage',
  [norm('ΝΟΣΗΛΕΙΑ')]: 'hospitalizationPercentage',
  [norm('ΔΙΑΓΝΩΣΤΙΚΕΣ ΕΞΕΤΑΣΕΙΣ - ΙΑΤΡΙΚΕΣ ΠΡΑΞΕΙΣ')]: 'medicalExaminationsPercentage',
  [norm('ΕΞΟΔΑ ΧΕΙΡΟΥΡΓΙΟΥ & ΑΝΑΙΣΘΗΣΙΑΣ')]: 'surgeryExpensesPercentage',
  [norm('ΑΜΟΙΒΕΣ ΙΑΤΡΩΝ, ΧΕΙΡΟΥΡΓΩΝ & ΑΝΑΙΣΘΗΣΙΟΛΟΓΩΝ')]: 'physicianFeesPercentage',
  [norm('ΛΟΙΠΑ')]: 'otherExpensesPercentage',
};

function emptyRow(provider: string): MedicalExpensesProviderPercentage {
  return {
    healthProvider: provider,
    medicalSuppliesPercentage: 0,
    specialMedicalSuppliesPercentage: 0,
    hospitalizationPercentage: 0,
    medicalExaminationsPercentage: 0,
    surgeryExpensesPercentage: 0,
    physicianFeesPercentage: 0,
    otherExpensesPercentage: 0,
  };
}


@Component({
  selector: 'app-provider-peer-comparisons',
  templateUrl: './provider-peer-comparisons.component.html',
  styleUrls: ['./provider-peer-comparisons.component.scss'],
  standalone: true,
  imports: [
    DashboardHeaderComponent,
    PaginatedTableComponent,
    SplineChartComponent,
    PanelModule,
    ButtonModule,
    DatePickerModule,
    FormsModule,
    //PanelComponent,
    TranslatePipe,
    DashboardFiltersComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProviderPeerComparisonsComponent implements OnInit, OnDestroy {
  private reportData = inject(ReportDataService);
  translateService = inject(TranslationService);

  rows: any;
  temp: any[] = [];
  tableData: any;

  loadingIndicator = true;
  reorderable = true;

  headers: any[] = [];
  rangeDates: any;
  provClaimHeaders: string[] = [];


  medicalExpensesDistHeaders: string[] = [];

  // filtersPanelConfig: PanelConfigI = {
  //   toggleable: true,
  //   collapsed: false,
  //   expandIcon: undefined,
  //   collapseIcon: undefined,
  //   headerIcon: 'fa-solid fa-filter',
  //   headerLabel : this.translateService.getTranslation('PROVIDER_PEER_COMPARISONS.FILTERS'),
  // };

  keys: any[] = [];

  timeout: any;

  showFilters: boolean = false;

  claimRateByProvider: ClaimRateByProvider = {} as ClaimRateByProvider;
  claimsByClinic: ClaimsByClinic = {} as ClaimsByClinic;
  providerClaims: ProviderClaim[] = [];
  medicalExpPerc: MedicalExpensesProviderPercentage[] = [];
  avgClaimPerDRG: AvgClaimDRG = {} as AvgClaimDRG;
  hospDurationPerDRG: HospDurationDRG = {} as HospDurationDRG;
  claimRate: any;

  private subscriptions: Subscription[] = [];
  dataLoaded = signal(false);
  translatedHeaders: Record<string, string> | undefined;
  private langChangeSub!: Subscription;

  total = 0;
  pageNumber = 1;
  pageSize = 15;
  loading = false;

  ngOnInit(): void {
    this.langChangeSub = this.translateService.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.loadMedicalExpensesDistHeaders();

      this.initializeView();
    });
    this.loadMedicalExpenses({ first: 0, rows: this.pageSize } as TableLazyLoadEvent);
  }



  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.langChangeSub.unsubscribe();
  }

  initializeView() {
    const claimRateByProvider$ = this.reportData.getClaimRateByProvider().pipe(
      catchError((error) => {
        console.error('Error fetching Claim Rates By Provider', error);
        const emptyResult: ClaimRateByProvider = {
          providers: [],
          dateAmount: [],
        };
        return of(emptyResult);
      })
    );

    const summaryByClinicClaim$ = this.reportData
      .getClaimsSummaryByClinic()
      .pipe(
        catchError((error) => {
          console.error('Error fetching Summary By Clinic Data', error);
          const emptyResult: ClaimsByClinic = {
            providers: [],
            clinicAmount: [],
          };
          return of(emptyResult);
        })
      );

    const providerClaims$ = this.reportData.getProviderClaims().pipe(
      catchError((error) => {
        console.error('Error fetching Provider Claims Data', error);
        return [];
      })
    );

    const avgClaimPerDRG$ = this.reportData.getAvgClaimPerDRG().pipe(
      catchError((error) => {
        console.error('Error fetching Average Claims By DRG Data', error);
        const emptyResult: AvgClaimDRG = {
          providers: [],
          drgAmount: [],
        };
        return of(emptyResult);
      })
    );

    const hospDurationPerDRG$ = this.reportData.getHospDurationPerDRG().pipe(
      catchError((error) => {
        console.error('Error fetching Hospital Duration By DRG Data', error);
        const emptyResult: HospDurationDRG = {
          providers: [],
          drgAmount: [],
        };
        return of(emptyResult);
      })
    );

    const clinicTranslation = this.translateService.getTranslation('PROVIDER_PEER_COMPARISONS.CLINIC');
    const drgTranslation = this.translateService.getTranslation('PROVIDER_PEER_COMPARISONS.DRG');


    const initialSubscriptions$ = forkJoin([
      claimRateByProvider$,
      summaryByClinicClaim$,
      providerClaims$,
      avgClaimPerDRG$,
      hospDurationPerDRG$,
    ]).subscribe({
      next: ([
        claimRateByProviderResult,
        summaryByClinicClaimResult,
        providerClaimsResult,
        avgClaimPerDRGResult,
        hospDurationPerDRGResult,
      ]) => {
        this.claimRateByProvider = claimRateByProviderResult;
        
        this.claimsByClinic = summaryByClinicClaimResult;
        this.claimsByClinic.providers = [
          clinicTranslation,
          ...this.claimsByClinic.providers,
        ];

        this.providerClaims = providerClaimsResult
          .map((m) => ({
            ...m,
            claimVolume: Math.round(m.claimVolume * 100) / 100,
            claimVolumePercentage:
              Math.round(m.claimVolumePercentage * 100) / 100,
            totalClaimsPrice: Math.round(m.totalClaimsPrice * 100) / 100,
            totalClaimsPercentage:
              Math.round(m.totalClaimsPercentage * 100) / 100,
          }))
          .sort((a, b) => a.healthProvider.localeCompare(b.healthProvider));


        this.avgClaimPerDRG = avgClaimPerDRGResult;
        this.avgClaimPerDRG.providers = [
          drgTranslation,
          ...this.avgClaimPerDRG.providers,
        ];

        this.avgClaimPerDRG.drgAmount.map((n) => {
          n.amounts = n.amounts.map((a) => Math.round(a * 100) / 100);
        });

        this.hospDurationPerDRG = hospDurationPerDRGResult;
        this.hospDurationPerDRG.providers = [
          drgTranslation,
          ...this.hospDurationPerDRG.providers,
        ];

        this.hospDurationPerDRG.drgAmount.map((n) => {
          n.amounts = n.amounts.map((a) => Math.round(a * 100) / 100);
        });
      },
      error: (error) => {
        console.error('Error:', error);
        this.dataLoaded.set(true);
      },
      complete: () => {
        this.dataLoaded.set(true);
      },
    });
    this.subscriptions.push(initialSubscriptions$);
  }

  getIconByLabel(label: string): string {
    switch (label) {
      case 'Total Claims':
        return 'fas fa-file-alt';
      case 'Pending Claims':
        return 'fas fa-hourglass-half';
      case 'Approved Claims':
        return 'fas fa-check';
      case 'Rejected Claims':
        return 'fa-solid fa-x';
      case 'Charged Claims':
        return 'fas fa-clock';
      case 'Total Claim Amount':
        return 'fas fa-exclamation-circle';
      default:
        return 'fas fa-question-circle';
    }
  }


  toggleFilters() {
    this.showFilters = !this.showFilters;
  }

  resetRows() {
    this.resetFields();
  }

  resetFields() {
    //reset the value to empty string
    let selectTag = document.querySelectorAll('select');
    selectTag.forEach((element) => {
      element.value = '';
    });

    //Clears ID field
    const id = document.querySelector('input[name="id"]') as HTMLInputElement;
    id.value = 'Filter by ID...';
    //Clears Date Field
    const fromDateInput = document.querySelector(
      'input[name="fromDate"]'
    ) as HTMLInputElement;
    const toDateInput = document.querySelector(
      'input[name="toDate"]'
    ) as HTMLInputElement;

    fromDateInput.value = '';
    toDateInput.value = '';
  }

  apply() { }

  /*Listens for key press  and updates the result*/
  @HostListener('document:keyup', ['$event'])
  handleEnterKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.apply();
    }
  }

  getTranslations(keys: string[]): Record<string, string> {
    const result: Record<string, string> = {};
    keys.forEach(key => {
      result[key] = this.translateService.getTranslation(key);
    });
    return result;
  }

  loadTranslations() {
    const keys = [
      'PROVIDER_PEER_COMPARISONS.HEALTH_PROVIDER',
      'PROVIDER_PEER_COMPARISONS.CLAIM_VOLUME',
      'PROVIDER_PEER_COMPARISONS.TOTAL_CLAIMS'
    ];

    this.translateService.getTranslations(keys).subscribe(translations => {
      this.provClaimHeaders = [
        translations['PROVIDER_PEER_COMPARISONS.HEALTH_PROVIDER'],
        translations['PROVIDER_PEER_COMPARISONS.CLAIM_VOLUME'] + ' (N)',
        translations['PROVIDER_PEER_COMPARISONS.CLAIM_VOLUME'] + ' (%)',
        translations['PROVIDER_PEER_COMPARISONS.TOTAL_CLAIMS'] + ' (€)',
        translations['PROVIDER_PEER_COMPARISONS.TOTAL_CLAIMS'] + ' (%)',
      ];
    });
  }

  loadMedicalExpensesDistHeaders() {
    const keys = [
      'PROVIDER_PEER_COMPARISONS.HEALTH_PROVIDER',
      'PROVIDER_PEER_COMPARISONS.MEDICAL_SUPPLIES',
      'PROVIDER_PEER_COMPARISONS.SPECIAL_MEDICAL_SUPPLIES',
      'PROVIDER_PEER_COMPARISONS.HOSPITALIZATION',
      'PROVIDER_PEER_COMPARISONS.MEDICAL_EXAMINATIONS',
      'PROVIDER_PEER_COMPARISONS.SURGERY_EXPENSES',
      'PROVIDER_PEER_COMPARISONS.PHYSICIAN_FEES',
      'PROVIDER_PEER_COMPARISONS.OTHER_EXPENSES',
    ];

    this.translateService.getTranslations(keys).subscribe(translations => {
      this.medicalExpensesDistHeaders = [
        translations['PROVIDER_PEER_COMPARISONS.HEALTH_PROVIDER'],
        `${translations['PROVIDER_PEER_COMPARISONS.MEDICAL_SUPPLIES']} (%)`,
        `${translations['PROVIDER_PEER_COMPARISONS.SPECIAL_MEDICAL_SUPPLIES']} (%)`,
        `${translations['PROVIDER_PEER_COMPARISONS.HOSPITALIZATION']} (%)`,
        `${translations['PROVIDER_PEER_COMPARISONS.MEDICAL_EXAMINATIONS']} (%)`,
        `${translations['PROVIDER_PEER_COMPARISONS.SURGERY_EXPENSES']} (%)`,
        `${translations['PROVIDER_PEER_COMPARISONS.PHYSICIAN_FEES']} (%)`,
        `${translations['PROVIDER_PEER_COMPARISONS.OTHER_EXPENSES']} (%)`,
      ];
    })
  }

  loadMedicalExpenses(event: TableLazyLoadEvent) {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.pageSize;
    this.pageNumber = Math.floor(first / rows) + 1;
    this.pageSize = rows;
    this.loading = true;
    const sub = this.reportData.getMedicalExpensesDistributionPercPage(this.pageNumber, rows)
      .pipe(
        map((resp: PaginatedProvidersResponse) => {
          this.total = resp?.total ?? 0;

          const mapped = (resp?.items ?? []).map(item => {
            const row = emptyRow(item.provider);
            for (const slice of item.categoriesDistribution ?? []) {
              const field = CATEGORY_TO_FIELD[norm(slice.category)];
              if (field) (row as any)[field] = pct(slice.percentage);
            }
            return row;
          });

          return mapped.map(m => ({
            ...m,
            medicalSuppliesPercentage: Math.round(m.medicalSuppliesPercentage * 100) / 100,
            specialMedicalSuppliesPercentage: Math.round(m.specialMedicalSuppliesPercentage * 100) / 100,
            hospitalizationPercentage: Math.round(m.hospitalizationPercentage * 100) / 100,
            medicalExaminationsPercentage: Math.round(m.medicalExaminationsPercentage * 100) / 100,
            surgeryExpensesPercentage: Math.round(m.surgeryExpensesPercentage * 100) / 100,
            physicianFeesPercentage: Math.round(m.physicianFeesPercentage * 100) / 100,
            otherExpensesPercentage: Math.round(m.otherExpensesPercentage * 100) / 100,
          }));
        }),
        catchError(err => {
          console.error('Error fetching paginated medical expenses', err);
          this.total = 0;
          return of<MedicalExpensesProviderPercentage[]>([]);
        })
      )
      .subscribe(rows => {
        this.medicalExpPerc = rows;
        this.loading = false;
        // this.cdr.markForCheck();
      });

    this.subscriptions.push(sub);

  }

}
